
import { useEffect, useState } from 'react';
import { PageViewTrackerHookProps, PageViewState, PageViewEventData } from './types';
import { sendPageViewToTrackingService } from './trackingService';
import { pushToDataLayer, hasPageViewBeenSent, markPageViewAsSent } from './utils';

export const usePageViewTracking = ({ shopId, organizationId }: PageViewTrackerHookProps) => {
  const [pageViewState, setPageViewState] = useState<PageViewState>({
    attempted: false,
    sent: {}
  });

  useEffect(() => {
    if (!organizationId) {
      console.log('⚠️ No organization ID available, skipping PageView tracking');
      return;
    }

    const pathname = window.location.pathname;
    
    // Check if we've already sent a page view for this path
    if (hasPageViewBeenSent(pathname)) {
      console.log(`📊 PageView already sent for ${pathname}, skipping`);
      return;
    }

    const trackPageView = async (attempt = 1) => {
      try {
        // Collect information about fbq availability
        const hasFbq = typeof window.fbq === 'function';
        const fbqLoaded = hasFbq && (window.fbq.loaded === true || window._fbq_called === true);
        
        // Get Pixel ID if available
        let pixelId = '';
        if (hasFbq && fbqLoaded && typeof window.fbq.getState === 'function') {
          try {
            const state = window.fbq.getState();
            pixelId = state?.pixelId || '';
          } catch (e) {
            console.warn('Error getting Meta Pixel ID:', e);
          }
        }
        
        // Collect some additional browser context
        const referrer = document.referrer || '';
        const screenResolution = `${window.screen.width}x${window.screen.height}`;
        const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
        
        // Extract any potential product information from the page
        // This is a basic implementation - should be enhanced for specific pages
        const productElements = document.querySelectorAll('[data-product-id]');
        const productIds = Array.from(productElements).map(el => el.getAttribute('data-product-id')).filter(Boolean);
        
        // Get content category from meta tags if available
        let contentCategory = '';
        const categoryMeta = document.querySelector('meta[property="og:category"]');
        if (categoryMeta) {
          contentCategory = categoryMeta.getAttribute('content') || '';
        }
        
        // Prepare event data with all diagnostic information
        const eventData: PageViewEventData = {
          page_path: pathname,
          page_title: document.title,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          sequence: `PAGE_VIEW_${attempt}`,
          attempt,
          has_fbq: hasFbq,
          fbq_loaded: fbqLoaded,
          referrer,
          screen_resolution: screenResolution,
          viewport_size: viewportSize,
          
          // Add Meta-specific fields
          content_name: document.title,
          content_category: contentCategory || 'page',
          content_ids: productIds.length > 0 ? productIds : undefined,
          content_type: productIds.length > 0 ? 'product' : 'page',
          
          // Add user agent and browser info
          client_user_agent: navigator.userAgent,
          browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        // Push to dataLayer first for GTM/GA
        pushToDataLayer(eventData);

        // Send to tracking service (conversion API)
        console.log(`📊 Sending PageView (attempt ${attempt}) to tracking service for ${pathname}`);
        const success = await sendPageViewToTrackingService(
          organizationId,
          shopId,
          eventData
        );

        if (success) {
          console.log(`✅ PageView tracking successful for ${pathname}`);
          setPageViewState(prev => ({
            attempted: true,
            sent: { ...prev.sent, [pathname]: true }
          }));
          markPageViewAsSent(pathname);
        } else if (attempt < 3) {
          console.log(`⚠️ PageView tracking failed for ${pathname}, will retry (attempt ${attempt})`);
          // Retry after a short delay
          setTimeout(() => {
            trackPageView(attempt + 1);
          }, 2000);
        } else {
          console.error(`❌ PageView tracking failed after ${attempt} attempts for ${pathname}`);
          setPageViewState(prev => ({
            attempted: true,
            sent: { ...prev.sent, [pathname]: false }
          }));
        }
      } catch (error) {
        console.error('❌ Error in PageView tracking:', error);
        setPageViewState(prev => ({
          attempted: true,
          sent: { ...prev.sent, [pathname]: false }
        }));
      }
    };

    trackPageView();
  }, [organizationId, shopId]);

  return pageViewState;
};
